import React, { useState, useEffect } from "react";
import api, { HR_BASE_URL } from "../api";
import styled from "styled-components";
import { Calendar, Download, RefreshCw, FileText } from "lucide-react";
import { CSVLink } from "react-csv";

const Page = styled.div`
  min-height: 100vh;
  padding: clamp(20px, 4vw, 40px);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  color: var(--muted);
  font-size: 14px;
`;

const FilterCard = styled.div`
  background: var(--glass);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Select = styled.select`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  background: var(--primary);
  color: white;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background: var(--accent);
  }
`;

const ExportBtn = styled(Button)`
  background: var(--success);
  &:hover {
    background: #059669;
  }
`;

const TableContainer = styled.div`
  background: var(--glass);
  backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow-x: auto;
  box-shadow: var(--shadow);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.02);
  color: var(--text);
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text);
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => {
    switch (props.status) {
      case "Present": return "rgba(16, 185, 129, 0.2)";
      case "Absent": return "rgba(239, 68, 68, 0.2)";
      case "Week Off": return "rgba(139, 92, 246, 0.2)";
      default: return "rgba(245, 158, 11, 0.2)";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "Present": return "#10b981";
      case "Absent": return "#ef4444";
      case "Week Off": return "#8b5cf6";
      default: return "#f59e0b";
    }
  }};
`;

function MyAttendanceReport() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);

  // Generate last 6 months for dropdown
  const getMonths = () => {
    const months = [];
    const date = new Date();
    for (let i = 0; i < 6; i++) {
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const label = date.toLocaleString("default", { month: "long", year: "numeric" });
      months.push({ value, label });
      date.setMonth(date.getMonth() - 1);
    }
    return months;
  };
  const [selectedMonth, setSelectedMonth] = useState(getMonths()[0].value);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const empId = localStorage.getItem("employee_id");
      const res = await api.get(`${HR_BASE_URL}employee/my-attendance/?employee_id=${empId}&month=${selectedMonth}`);
      setReport(res.data.report || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [selectedMonth]);

  const csvHeaders = [
    { label: "Date", key: "date" },
    { label: "Shift", key: "shift_name" },
    { label: "Timings", key: "shift_timing" },
    { label: "Check In", key: "check_in" },
    { label: "Check Out", key: "check_out" },
    { label: "Total Hours", key: "total_hours" },
    { label: "Status", key: "status" },
  ];

  return (
    <Page>
      <Container>
        <Header>
          <div>
            <Title>
              <Calendar size={32} color="var(--primary)" />
              My Attendance
            </Title>
            <Subtitle>View your monthly attendance report</Subtitle>
          </div>
          {report.length > 0 && (
            <CSVLink data={report} headers={csvHeaders} filename={`Attendance_${selectedMonth}.csv`} style={{ textDecoration: 'none' }}>
              <ExportBtn>
                <Download size={18} />
                Export CSV
              </ExportBtn>
            </CSVLink>
          )}
        </Header>

        <FilterCard>
          <Calendar size={20} color="var(--muted)" />
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {getMonths().map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </Select>
          <Button onClick={fetchReport} disabled={loading}>
            <RefreshCw size={18} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </Button>
        </FilterCard>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Shift</Th>
                <Th>Timings</Th>
                <Th>Check In</Th>
                <Th>Check Out</Th>
                <Th>Total Hours</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {report.map((row, idx) => (
                <tr key={idx}>
                  <Td>{row.date}</Td>
                  <Td>{row.shift_name}</Td>
                  <Td>{row.shift_timing}</Td>
                  <Td>{row.check_in}</Td>
                  <Td>{row.check_out}</Td>
                  <Td>{row.total_hours}</Td>
                  <Td>
                    <StatusBadge status={row.status}>{row.status}</StatusBadge>
                  </Td>
                </tr>
              ))}
              {report.length === 0 && !loading && (
                <tr>
                  <Td colSpan="7" style={{ textAlign: "center", padding: "32px" }}>
                    No attendance records found for this month.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Container>
    </Page>
  );
}

export default MyAttendanceReport;
